import { useState } from "react"
import { Link } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../redux/hooks"
import { forgotPassword, clearError } from "../redux/slices/authSlice"
import { errorMessage } from "../utils/mutationResult"
import { toast } from "../hooks/use-toast"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Alert, AlertDescription } from "../components/ui/alert"
import { ArrowLeft, Mail, Loader2, CheckCircle, WifiOff } from "lucide-react"

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("")
  const [submitted, setSubmitted] = useState(false)

  const dispatch = useAppDispatch()
  const { loading, error } = useAppSelector((state) => state.auth)
  // Sending a reset link always requires a live request, so surface the
  // offline state up front instead of letting the user submit into a
  // request that can never complete.
  const isOnline = useAppSelector((state) => state.offline?.isOnline ?? true)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isOnline) return
    dispatch(clearError())

    try {
      await dispatch(forgotPassword(email)).unwrap()
      setSubmitted(true)
    } catch (err) {
      toast.error("Unable to send reset link", errorMessage(err, "Failed to send reset email"))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:bg-none dark:bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2 dark:text-foreground">EncoAudit</h1>
          <p className="text-slate-600 dark:text-muted-foreground">Reset your password</p>
        </div>

        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              {submitted ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <Mail className="w-6 h-6 text-blue-600 dark:text-link" />
              )}
            </div>
            <CardTitle className="text-xl font-semibold text-slate-900 dark:text-foreground">
              {submitted ? "Check Your Email" : "Forgot Password"}
            </CardTitle>
          </CardHeader>

          <CardContent>
            {submitted ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-slate-600 dark:text-muted-foreground">
                  If an account with <span className="font-medium text-slate-800 dark:text-foreground">{email}</span> exists, a password reset link has been sent. Please check your inbox and spam folder.
                </p>
                <p className="text-xs text-slate-500 dark:text-muted-foreground">
                  The link will expire in 10 minutes.
                </p>
                <Button
                  variant="outline"
                  className="w-full h-11"
                  onClick={() => { setSubmitted(false); setEmail("") }}
                >
                  Send Again
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-slate-600 dark:text-muted-foreground">
                  Enter the email address associated with your account and we will send you a link to reset your password.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-foreground">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="h-11"
                    autoFocus
                  />
                </div>

                {!isOnline && (
                  <Alert variant="destructive">
                    <WifiOff className="h-4 w-4" />
                    <AlertDescription>You&apos;re offline. Reconnect to the internet to send a reset link.</AlertDescription>
                  </Alert>
                )}

                {isOnline && error && (
                  <Alert variant="destructive">
                    <AlertDescription>{errorMessage(error, "Failed to send reset email")}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border hover:bg-blue-700 text-white font-medium"
                  disabled={loading || !isOnline}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : !isOnline ? (
                    <>
                      <WifiOff className="w-4 h-4 mr-2" />
                      Offline
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </form>
            )}

            <div className="mt-4 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-muted-foreground"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
